import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { usePermissions } from "../../../PermissionsProvider";
import { notification } from "antd";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import {
  ImprovementsService,
  ContinuousLearningRun,
  EligibleModel,
  ExampleOodPoint,
  NON_TERMINAL_STATUSES,
  CLRunStatus,
  ActionSummary,
} from "../../../services/ImprovementsService";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Alert, AlertDescription, AlertTitle } from "../../ui/alert";
import {
  ArrowLeftIcon,
  PlayIcon,
  XIcon,
  CheckCircleIcon,
  InfoIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  Loader2Icon,
  MoreHorizontalIcon,
  RefreshCwIcon,
  TrashIcon,
  ExternalLinkIcon,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import moment from "moment";
import { getFinetuneStorageKey } from "../../Finetune/FinetuneRun/common";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function useDarkMode(): boolean {
  const [isDark, setIsDark] = useState(
    () => document.documentElement.classList.contains("dark"),
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);
  return isDark;
}

const POLL_INTERVAL_MS = 5000;

function statusBadgeClasses(status: CLRunStatus): string {
  switch (status) {
    case "PENDING":
      return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
    case "ANALYZING":
    case "ACTIONING":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400";
    case "ANALYZED":
      return "bg-sky-100 text-sky-800 dark:bg-sky-900/20 dark:text-sky-400";
    case "ACTIONED":
      return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
    case "DISMISSED":
      return "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400";
    case "FAILED":
      return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
  }
}

const StatusBadge: React.FC<{ status: CLRunStatus }> = ({ status }) => {
  const isActive = NON_TERMINAL_STATUSES.includes(status);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${statusBadgeClasses(status)}`}>
      {isActive && <Loader2Icon className="h-3 w-3 animate-spin" />}
      {status}
    </span>
  );
};

const INTERNAL_COMPANY_IDS = [2, 51];

const ImprovementDetail: React.FC = () => {
  const { apiKey, companyId } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const { modelId } = useParams<{ modelId: string }>();

  const isInternal = useMemo(
    () => !!companyId && INTERNAL_COMPANY_IDS.includes(companyId),
    [companyId],
  );

  const stateModel: EligibleModel | undefined = (location.state as any)?.model;
  const [fetchedModel, setFetchedModel] = useState<EligibleModel | null>(null);
  const [modelLoading, setModelLoading] = useState(!stateModel);

  const model = stateModel ?? fetchedModel ?? undefined;

  useEffect(() => {
    if (stateModel || !modelId) return;
    ImprovementsService.getModelById(
      parseInt(modelId),
      setModelLoading,
      apiKey,
    ).subscribe(
      (data: EligibleModel) => setFetchedModel(data ?? null),
      () => setModelLoading(false),
    );
  }, [stateModel, modelId, apiKey]);

  const [runs, setRuns] = useState<ContinuousLearningRun[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedRunId, setExpandedRunId] = useState<number | null>(null);
  const [sourceFinetuneRun, setSourceFinetuneRun] = useState<Record<string, any> | null>(null);

  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [minOod, setMinOod] = useState(1000);
  const [isTriggering, setIsTriggering] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const scope = useMemo(() => {
    if (!model) return undefined;
    if (model.scope_type === "INTENT_GROUP" && model.intent_group_id) {
      return { intentGroupId: model.intent_group_id };
    }
    if (model.application_id && model.intent_id) {
      return { applicationId: model.application_id, intentId: model.intent_id };
    }
    return undefined;
  }, [model]);

  useEffect(() => {
    if (!model?.finetune_run_id) return;
    ImprovementsService.getFinetuneRun(
      model.finetune_run_id,
      undefined,
      apiKey
    ).subscribe(
      (response: Record<string, any>) => setSourceFinetuneRun(response || null),
      () => {}
    );
  }, [apiKey, model?.finetune_run_id]);

  const fetchRuns = useCallback(() => {
    if (!scope) return;
    ImprovementsService.getRuns(
      isLoading ? undefined : setIsLoading,
      apiKey,
      scope,
      50,
      0
    ).subscribe(
      (response: ContinuousLearningRun[]) => setRuns(response || []),
      () => {
        notification.error({
          message: "Error",
          description: "Failed to load runs",
        });
      }
    );
  }, [apiKey, scope]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  const hasActiveRuns = useMemo(
    () => runs.some((r) => NON_TERMINAL_STATUSES.includes(r.status)),
    [runs]
  );

  useEffect(() => {
    if (hasActiveRuns) {
      pollRef.current = setInterval(fetchRuns, POLL_INTERVAL_MS);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [hasActiveRuns, fetchRuns]);

  const handleTrigger = () => {
    if (!scope) return;
    setIsTriggering(true);
    ImprovementsService.trigger(
      setIsTriggering,
      apiKey,
      scope,
      minOod,
      dateStart ? `${dateStart}T00:00:00` : undefined,
      dateEnd ? `${dateEnd}T23:59:59` : undefined
    ).subscribe(
      () => {
        notification.success({
          message: "Analysis Dispatched",
          description: "A new analysis run has been queued.",
        });
        fetchRuns();
      },
      () => {
        setIsTriggering(false);
        notification.error({
          message: "Error",
          description: "Failed to trigger analysis",
        });
      }
    );
  };

  const handleAction = (runId: number, action: "create_dataset" | "dismiss") => {
    ImprovementsService.takeAction(runId, action, undefined, apiKey).subscribe(
      () => {
        notification.success({
          message: action === "dismiss" ? "Dismissed" : "Action Dispatched",
          description:
            action === "dismiss"
              ? "Run dismissed."
              : "Dataset creation has been queued.",
        });
        fetchRuns();
      },
      () => {
        notification.error({
          message: "Error",
          description: `Failed to ${action === "dismiss" ? "dismiss" : "process"} this run.`,
        });
      }
    );
  };

  const handleRerun = (runId: number) => {
    ImprovementsService.rerunRun(runId, undefined, apiKey).subscribe(
      () => {
        notification.success({
          message: "Rerun Dispatched",
          description: "A new analysis run has been queued with the same parameters.",
        });
        fetchRuns();
      },
      () => {
        notification.error({
          message: "Error",
          description: "Failed to rerun analysis.",
        });
      }
    );
  };

  const handleDelete = (runId: number) => {
    ImprovementsService.deleteRun(runId, undefined, apiKey).subscribe(
      () => {
        notification.success({ message: "Deleted", description: "Run has been removed." });
        setRuns((prev) => prev.filter((r) => r.id !== runId));
        if (expandedRunId === runId) setExpandedRunId(null);
      },
      () => {
        notification.error({ message: "Error", description: "Failed to delete run." });
      }
    );
  };

  if (!isInternal) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">
          This page is only available to internal Maitai users.
        </p>
      </div>
    );
  }

  if (modelLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-5xl mx-auto px-8 py-8 flex items-center gap-2 text-muted-foreground">
          <Loader2Icon className="h-4 w-4 animate-spin" /> Loading model...
        </div>
      </div>
    );
  }

  if (!model) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-5xl mx-auto px-8 py-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin/improvements")}
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Models
          </Button>
          <p className="mt-4 text-muted-foreground">
            Model not found. Please go back and select a model.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin/improvements")}
        >
          <ArrowLeftIcon className="mr-2 h-4 w-4" />
          Back to Models
        </Button>

        {/* Model Header */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                  {model.scope_type === "INTENT_GROUP"
                    ? model.intent_group_name || `Intent Group ${model.intent_group_id}`
                    : `${model.application_name || `App ${model.application_id}`} / ${model.intent_name || `Intent ${model.intent_id}`}`}
                </h1>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  model.scope_type === "INTENT_GROUP"
                    ? "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
                    : "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400"
                }`}>
                  {model.scope_type === "INTENT_GROUP" ? "Intent Group" : "App + Intent"}
                </span>
              </div>
              <p className="text-muted-foreground text-sm">
                {model.company_name} &middot;{" "}
                <span className="font-mono text-foreground">{model.model}</span> &middot; Base:{" "}
                {model.base_model}
              </p>
              <div className="flex items-center gap-3 text-sm">
                <span
                  className="text-primary hover:underline cursor-pointer"
                  onClick={() => navigate(`/forge/finetuning/${model.finetune_run_id}`)}
                >
                  Source Finetune #{model.finetune_run_id}
                </span>
                {sourceFinetuneRun && (
                  <>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      sourceFinetuneRun.status === "COMPLETED"
                        ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                        : sourceFinetuneRun.status === "RUNNING"
                        ? "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
                        : sourceFinetuneRun.status === "FAILED"
                        ? "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"
                        : "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400"
                    }`}>
                      {sourceFinetuneRun.status}
                    </span>
                    {sourceFinetuneRun.date_created && (
                      <span className="text-muted-foreground text-xs">
                        {moment(sourceFinetuneRun.date_created).format("MMM D, YYYY HH:mm")}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Ad-hoc Trigger */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Run Ad-hoc Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-4 flex-wrap">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Start</label>
                <Input
                  type="date"
                  value={dateStart}
                  onChange={(e) => setDateStart(e.target.value)}
                  className="w-40"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">End</label>
                <Input
                  type="date"
                  value={dateEnd}
                  onChange={(e) => setDateEnd(e.target.value)}
                  className="w-40"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">
                  Min OOD
                </label>
                <Input
                  type="number"
                  value={minOod}
                  onChange={(e) =>
                    setMinOod(parseInt(e.target.value, 10) || 1000)
                  }
                  className="w-28"
                  min={1}
                />
              </div>
              <Button onClick={handleTrigger} disabled={isTriggering}>
                {isTriggering ? (
                  <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <PlayIcon className="mr-2 h-4 w-4" />
                )}
                Run Analysis
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Leave dates blank to analyze the last 24 hours.
            </p>
          </CardContent>
        </Card>

        {/* Run History */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Run History</CardTitle>
              {hasActiveRuns && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2Icon className="h-3 w-3 animate-spin" />
                  Refreshing...
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isLoading && runs.length === 0 ? (
              <div className="animate-pulse space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-10 bg-muted rounded" />
                ))}
              </div>
            ) : runs.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                No runs yet. Trigger an analysis above or wait for the daily
                schedule.
              </p>
            ) : (
              <div className="space-y-1">
                {runs.map((run) => (
                  <RunRow
                    key={run.id}
                    run={run}
                    isExpanded={expandedRunId === run.id}
                    onToggle={() =>
                      setExpandedRunId(
                        expandedRunId === run.id ? null : run.id
                      )
                    }
                    onAction={handleAction}
                    onRerun={handleRerun}
                    onDelete={handleDelete}
                    fetchRuns={fetchRuns}
                    navigate={navigate}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};


function driftLabel(run: ContinuousLearningRun): { text: string; className: string } {
  if (run.drift_detected)
    return { text: "Drift", className: "text-red-600 dark:text-red-400" };
  if (run.recommendation === "FINETUNE_RECOMMENDED")
    return { text: "OOD Detected", className: "text-amber-600 dark:text-amber-400" };
  if (run.ood_count > 0 && run.recommendation === "NONE")
    return { text: "Insufficient Data", className: "text-amber-600 dark:text-amber-400" };
  return { text: "No Drift", className: "text-green-600 dark:text-green-400" };
}

const RunRow: React.FC<{
  run: ContinuousLearningRun;
  isExpanded: boolean;
  onToggle: () => void;
  onAction: (runId: number, action: "create_dataset" | "dismiss") => void;
  onRerun: (runId: number) => void;
  onDelete: (runId: number) => void;
  fetchRuns: () => void;
  navigate: ReturnType<typeof useNavigate>;
}> = ({ run, isExpanded, onToggle, onAction, onRerun, onDelete, fetchRuns, navigate }) => {
  const drift = driftLabel(run);

  return (
    <div className="border border-border rounded-lg">
      <div className="flex items-center">
        <button
          onClick={onToggle}
          className="flex items-center gap-4 flex-1 min-w-0 px-4 py-3 hover:bg-muted/50 transition-colors text-left"
        >
          {isExpanded ? (
            <ChevronDownIcon className="h-4 w-4 text-muted-foreground shrink-0" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 text-muted-foreground shrink-0" />
          )}
          <span className="text-xs text-muted-foreground w-52 shrink-0 whitespace-nowrap">
            {moment(run.date_window_start).format("MMM D, HH:mm")} —{" "}
            {moment(run.date_window_end).format("MMM D, HH:mm")}
          </span>
          {run.status === "PENDING" || run.status === "ANALYZING" ? (
            <span className="text-xs text-muted-foreground italic w-76 shrink-0">
              Analyzing...
            </span>
          ) : (
            <>
              <span className="text-xs w-40 shrink-0">
                <span className="text-muted-foreground">Finetune: </span>
                <span className={`font-semibold ${
                  run.recommendation === "FINETUNE_RECOMMENDED"
                    ? "text-red-600 dark:text-red-400"
                    : "text-green-600 dark:text-green-400"
                }`}>
                  {run.recommendation === "FINETUNE_RECOMMENDED" ? "Recommended" : "Not Needed"}
                </span>
              </span>
              <span className="text-xs w-36 shrink-0">
                <span className="text-muted-foreground">Reason: </span>
                <span className={`font-medium ${drift.className}`}>{drift.text}</span>
              </span>
            </>
          )}
        </button>

        <div className="flex items-center gap-2 px-4 shrink-0">
          <StatusBadge status={run.status} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontalIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onRerun(run.id)}>
                <RefreshCwIcon className="h-3.5 w-3.5 mr-2" />
                Rerun Analysis
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => onDelete(run.id)}
              >
                <TrashIcon className="h-3.5 w-3.5 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {isExpanded && <ExpandedRunDetail run={run} onAction={onAction} navigate={navigate} fetchRuns={fetchRuns} />}
    </div>
  );
};


const ExpandedRunDetail: React.FC<{
  run: ContinuousLearningRun;
  onAction: (runId: number, action: "create_dataset" | "dismiss") => void;
  navigate: ReturnType<typeof useNavigate>;
  fetchRuns: () => void;
}> = ({ run, onAction, navigate, fetchRuns }) => {
  const canAct = run.status === "ANALYZED";

  return (
    <div className="border-t border-border px-4 py-4 space-y-4 bg-muted/30">
      {/* Error display */}
      {run.status === "FAILED" && (run.error_message || run.error) && (
        <Alert variant="destructive">
          <AlertTitle>Analysis Failed</AlertTitle>
          <AlertDescription>{run.error_message || run.error}</AlertDescription>
        </Alert>
      )}

      {/* Summary metrics */}
      <div className="grid grid-cols-3 gap-4">
        {/* OOD percentage */}
        <OodPercentageCard oodCount={run.ood_count} totalPoints={run.total_prod_points} />
        {/* PSI with color bar */}
        <DriftMetricCard
          label="PSI"
          value={run.psi}
          tooltip="Population Stability Index — measures how much the production data distribution has shifted from training. < 0.1 = stable, 0.1–0.25 = moderate, > 0.25 = significant drift."
          thresholds={[0.1, 0.25]}
          format={(v) => v.toFixed(4)}
          sublabels={["Stable", "Moderate", "Significant"]}
        />
        {/* Wasserstein with color bar */}
        <DriftMetricCard
          label="Wasserstein"
          value={run.wasserstein}
          tooltip="Earth Mover's distance between training and production distributions. Higher values indicate the production data is distributed differently from what the model was trained on."
          thresholds={[0.05, 0.15]}
          format={(v) => v.toFixed(6)}
          sublabels={["Low", "Moderate", "High"]}
        />
      </div>

      {/* Distribution chart */}
      {run.prod_centroid_distances && (
        <DistributionChart run={run} />
      )}

      {/* Tag breakdown */}
      {run.tag_summary && Object.keys(run.tag_summary).length > 0 && (
        <TagBreakdown tagSummary={run.tag_summary} />
      )}

      {/* Example OOD points */}
      {run.example_ood_points && run.example_ood_points.length > 0 && (
        <ExampleOodTable examples={run.example_ood_points} />
      )}

      {/* Action buttons */}
      {canAct && (
        <ActionButtons run={run} onAction={onAction} />
      )}

      {/* Actioning state */}
      {run.status === "ACTIONING" && (
        <Alert>
          <Loader2Icon className="h-4 w-4 animate-spin" />
          <AlertTitle>Creating datasets...</AlertTitle>
          <AlertDescription>
            Splitting OOD data, creating train/test datasets and golden test set.
          </AlertDescription>
        </Alert>
      )}

      {run.status === "ACTIONED" && (
        <ActionedDetail run={run} navigate={navigate} onFinetuneCreated={fetchRuns} />
      )}

      {run.status === "DISMISSED" && (
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          <XIcon className="h-4 w-4" /> Dismissed — no action taken.
        </p>
      )}
    </div>
  );
};


const ActionButtons: React.FC<{
  run: ContinuousLearningRun;
  onAction: (runId: number, action: "create_dataset" | "dismiss") => void;
}> = ({ run, onAction }) => {
  const trainCount = Math.floor((run.ood_request_ids?.length || run.ood_count) * 0.8);
  const testCount = (run.ood_request_ids?.length || run.ood_count) - trainCount;

  return (
    <div className="space-y-3">
      {run.synthetic_gap > 0 && (
        <Alert>
          <InfoIcon className="h-4 w-4" />
          <AlertTitle>Synthetic data needed</AlertTitle>
          <AlertDescription>
            {run.ood_count.toLocaleString()} OOD points will be split into ~{trainCount.toLocaleString()} train
            / ~{testCount.toLocaleString()} test. ~{run.synthetic_gap.toLocaleString()} synthetic
            examples will be generated to reach the minimum.
          </AlertDescription>
        </Alert>
      )}
      {run.synthetic_gap === 0 && (
        <p className="text-sm text-muted-foreground">
          {run.ood_count.toLocaleString()} OOD points will be split ~{trainCount.toLocaleString()} train
          / ~{testCount.toLocaleString()} test. A golden test set will be created.
        </p>
      )}
      <div className="flex items-center gap-3">
        <Button onClick={() => onAction(run.id, "create_dataset")}>
          <PlayIcon className="mr-2 h-4 w-4" />
          Create Datasets
        </Button>
        <Button variant="outline" onClick={() => onAction(run.id, "dismiss")}>
          Dismiss
        </Button>
      </div>
    </div>
  );
};


const ActionedDetail: React.FC<{
  run: ContinuousLearningRun;
  navigate: ReturnType<typeof useNavigate>;
  onFinetuneCreated: () => void;
}> = ({ run, navigate }) => {
  const { apiKey, companyId } = usePermissions();
  const [summary, setSummary] = useState<ActionSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    ImprovementsService.getActionSummary(run.id, undefined, apiKey).subscribe(
      (data: ActionSummary) => {
        setSummary(data);
        setLoading(false);
      },
      () => setLoading(false),
    );
  }, [run.id, apiKey]);

  const handleReviewFinetune = () => {
    if (!summary?.composition || !summary.source_finetune_run) return;

    const src = summary.source_finetune_run;
    const meta = src.meta ?? {};

    const finetuneData = {
      composition_id: summary.composition.id,
      application_id: run.application_id,
      intent_id: run.intent_id,
      intent_group_id: run.intent_group_id,
      prompt: src.prompt ?? "",
      fourbit: src.fourbit ?? false,
      base_model: src.base_model,
      display_name: `${src.display_name} (CL)`,
      description: src.description ?? `Continuous learning from run #${run.id}`,
      prompt_deletions: src.prompt_deletions ?? [],
      warmup_steps: meta.warmup_steps,
      warmup_ratio: meta.warmup_ratio,
      learning_rate: meta.learning_rate,
      per_device_train_batch_size: meta.per_device_train_batch_size,
      gradient_accumulation_steps: meta.gradient_accumulation_steps,
      train_on_responses_only: meta.train_on_responses_only,
      train_split: meta.train_split,
      alpha: meta.alpha,
      rank: meta.rank,
      epochs: meta.num_train_epochs,
      weight_decay: meta.weight_decay,
      dropout: meta.dropout,
      rslora: meta.rslora,
      num_gpus: meta.num_gpus,
      auto_test_set_ids: summary.test_sets.map((ts) => ts.id),
    };

    localStorage.setItem(
      getFinetuneStorageKey(companyId),
      JSON.stringify(finetuneData),
    );

    navigate(
      `/forge/finetuning/new?step=5&composition_id=${summary.composition.id}`,
    );
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
        <Loader2Icon className="h-4 w-4 animate-spin" /> Loading summary...
      </div>
    );
  }

  if (!summary) return null;

  const frnId = summary.triggered_finetune_run_id || run.triggered_finetune_run_id;

  return (
    <div className="space-y-4">
      <p className="text-sm flex items-center gap-2 text-green-700 dark:text-green-400">
        <CheckCircleIcon className="h-4 w-4" /> Datasets created successfully.
      </p>

      {/* Composition table */}
      {summary.composition && (
        <div className="border border-border rounded-md overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 bg-muted/50">
            <p className="text-xs font-medium text-foreground">
              Composition #{summary.composition.id}
              {summary.composition.name && (
                <span className="ml-1.5 text-muted-foreground font-normal">
                  {summary.composition.name}
                </span>
              )}
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => navigate(`/forge/compositions/${summary.composition!.id}`)}
            >
              View <ExternalLinkIcon className="ml-1 h-3 w-3" />
            </Button>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="px-3 py-1.5 text-left font-medium">Dataset</th>
                <th className="px-3 py-1.5 text-right font-medium w-24">Requests</th>
                <th className="px-3 py-1.5 text-right font-medium w-24">Source</th>
              </tr>
            </thead>
            <tbody>
              {summary.composition.members.map((m) => (
                <tr key={m.dataset_id} className="border-b border-border last:border-0">
                  <td className="px-3 py-1.5">
                    <button
                      className="text-primary hover:underline text-left"
                      onClick={() => navigate(`/forge/datasets/${m.dataset_id}`)}
                    >
                      {m.dataset_name || `Dataset #${m.dataset_id}`}
                    </button>
                    <span className="ml-1 text-muted-foreground">#{m.dataset_id}</span>
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-foreground">
                    {m.request_count.toLocaleString()}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <SourceBadge source={m.source} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Test sets table */}
      {summary.test_sets.length > 0 && (
        <div className="border border-border rounded-md overflow-hidden">
          <div className="px-3 py-2 bg-muted/50">
            <p className="text-xs font-medium text-foreground">Test Sets</p>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="px-3 py-1.5 text-left font-medium">Test Set</th>
                <th className="px-3 py-1.5 text-right font-medium w-24">Requests</th>
                <th className="px-3 py-1.5 text-right font-medium w-24">Type</th>
              </tr>
            </thead>
            <tbody>
              {summary.test_sets.map((ts) => (
                <tr key={ts.id} className="border-b border-border last:border-0">
                  <td className="px-3 py-1.5">
                    {ts.intent_group_id ? (
                      <button
                        className="text-primary hover:underline text-left"
                        onClick={() => navigate(`/intent-group/${ts.intent_group_id}/testsets/${ts.id}`)}
                      >
                        {ts.name || `Test Set #${ts.id}`}
                      </button>
                    ) : (
                      <span className="text-foreground">{ts.name || `Test Set #${ts.id}`}</span>
                    )}
                    <span className="ml-1 text-muted-foreground">#{ts.id}</span>
                    {ts.is_golden && (
                      <span className="ml-1.5 text-amber-600 dark:text-amber-400 text-[10px]">
                        Golden
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-foreground">
                    {ts.request_count.toLocaleString()}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <SourceBadge source={ts.type} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Finetune action */}
      {frnId ? (
        <div className="flex items-center gap-2">
          <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-primary"
            onClick={() => navigate(`/forge/finetuning/${frnId}`)}
          >
            Finetune Run #{frnId}
            <ExternalLinkIcon className="ml-1 h-3 w-3" />
          </Button>
        </div>
      ) : (
        <Button
          onClick={handleReviewFinetune}
          disabled={!summary?.composition || !summary?.source_finetune_run}
        >
          <PlayIcon className="mr-2 h-4 w-4" />
          Review &amp; Start Finetune
        </Button>
      )}
    </div>
  );
};


const SourceBadge: React.FC<{ source: string }> = ({ source }) => {
  const cls =
    source === "Original"
      ? "bg-secondary text-secondary-foreground"
      : source === "New (OOD)" || source === "New (Golden)"
      ? "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
      : "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400";
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${cls}`}>
      {source}
    </span>
  );
};


const OodPercentageCard: React.FC<{ oodCount: number; totalPoints: number }> = ({
  oodCount,
  totalPoints,
}) => {
  const pct = totalPoints > 0 ? (oodCount / totalPoints) * 100 : 0;
  const pctColor =
    pct >= 50
      ? "text-red-600 dark:text-red-400"
      : pct >= 20
      ? "text-amber-600 dark:text-amber-400"
      : "text-green-600 dark:text-green-400";

  return (
    <div className="space-y-1">
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1">
        Out of Distribution
        <span className="cursor-help" title="Percentage of production data points whose cosine distance to the model's training centroid exceeds the p95 threshold.">
          <InfoIcon className="h-3 w-3 text-muted-foreground/50 hover:text-muted-foreground transition-colors" />
        </span>
      </p>
      <p className={`text-xl font-bold tabular-nums ${pctColor}`}>
        {pct.toFixed(1)}%
      </p>
      <p className="text-[10px] text-muted-foreground tabular-nums">
        {oodCount.toLocaleString()} / {totalPoints.toLocaleString()}
      </p>
    </div>
  );
};


const DriftMetricCard: React.FC<{
  label: string;
  value: number | null | undefined;
  tooltip: string;
  thresholds: [number, number];
  format: (v: number) => string;
  sublabels: [string, string, string];
}> = ({ label, value, tooltip, thresholds, format, sublabels }) => {
  const hasValue = value != null;
  const level = !hasValue ? 0 : value < thresholds[0] ? 0 : value < thresholds[1] ? 1 : 2;

  const textColors = [
    "text-green-600 dark:text-green-400",
    "text-amber-600 dark:text-amber-400",
    "text-red-600 dark:text-red-400",
  ];

  const barMax = thresholds[1] * 2;
  const pct = hasValue ? Math.min((value / barMax) * 100, 100) : 0;
  const t1Pct = (thresholds[0] / barMax) * 100;
  const t2Pct = (thresholds[1] / barMax) * 100;

  return (
    <div className="space-y-1.5">
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1">
        {label}
        <span className="cursor-help" title={tooltip}>
          <InfoIcon className="h-3 w-3 text-muted-foreground/50 hover:text-muted-foreground transition-colors" />
        </span>
      </p>
      <p className={`text-lg font-bold ${hasValue ? textColors[level] : "text-foreground"}`}>
        {hasValue ? sublabels[level] : "—"}
      </p>
      {hasValue && (() => {
        const alignItems = pct < 15 ? "items-start" : pct > 85 ? "items-end" : "items-center";
        const translateX = pct < 15 ? "0%" : pct > 85 ? "-100%" : "-50%";
        return (
        <div className="space-y-0.5 max-w-[90%] overflow-hidden">
          <div className="relative h-4" style={{ marginBottom: "-2px" }}>
            <div
              className={`absolute flex flex-col ${alignItems}`}
              style={{ left: `${pct}%`, transform: `translateX(${translateX})` }}
            >
              <span className={`text-[9px] font-bold tabular-nums ${textColors[level]}`}>
                {format(value)}
              </span>
              <div
                className="w-0 h-0"
                style={{
                  borderLeft: "4px solid transparent",
                  borderRight: "4px solid transparent",
                  borderTop: "4px solid currentColor",
                }}
              />
            </div>
          </div>
          <div className="relative h-2 rounded-full"
            style={{ background: `linear-gradient(to right, #22c55e 0%, #22c55e ${t1Pct}%, #eab308 ${t1Pct}%, #eab308 ${t2Pct}%, #ef4444 ${t2Pct}%, #ef4444 100%)` }}
          >
            <div
              className="absolute top-[-2px] w-1.5 h-[calc(100%+4px)] bg-white dark:bg-gray-200 rounded-sm shadow-md border border-black/30 dark:border-white/40"
              style={{ left: `calc(${pct}% - 3px)` }}
            />
          </div>
          <div className="relative h-3 text-[9px] tabular-nums text-muted-foreground">
            <span className="absolute" style={{ left: `${t1Pct}%`, transform: "translateX(-50%)" }}>
              {thresholds[0]}
            </span>
            <span className="absolute" style={{ left: `${t2Pct}%`, transform: "translateX(-50%)" }}>
              {thresholds[1]}
            </span>
          </div>
        </div>
        );
      })()}
    </div>
  );
};


const DistributionChart: React.FC<{ run: ContinuousLearningRun }> = ({ run }) => {
  const isDark = useDarkMode();
  const chartData = useMemo(() => {
    const prodCd = run.prod_centroid_distances;
    if (!prodCd?.histogram_bins || !prodCd?.histogram_counts) return null;
    const bins = prodCd.histogram_bins;
    const labels = bins.map((b, i) =>
      i < bins.length - 1
        ? `${b.toFixed(3)}–${bins[i + 1].toFixed(3)}`
        : `≥${b.toFixed(3)}`
    );

    return {
      labels,
      datasets: [
        {
          label: "Production",
          data: prodCd.histogram_counts,
          backgroundColor: isDark ? "rgba(96, 165, 250, 0.5)" : "rgba(59, 130, 246, 0.7)",
          borderColor: isDark ? "rgb(96, 165, 250)" : "rgb(59, 130, 246)",
          borderWidth: 1,
        },
      ],
    };
  }, [run.prod_centroid_distances, isDark]);

  if (!chartData) return null;

  const gridColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const tickColor = isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)";

  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground mb-2">
        Distance Distribution (Production)
      </p>
      <div className="h-48">
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false } },
            scales: {
              x: {
                ticks: { maxRotation: 45, font: { size: 9 }, color: tickColor },
                grid: { color: gridColor },
              },
              y: {
                beginAtZero: true,
                ticks: { color: tickColor },
                grid: { color: gridColor },
              },
            },
          }}
        />
      </div>
    </div>
  );
};


const TagBreakdown: React.FC<{ tagSummary: Record<string, number> }> = ({
  tagSummary,
}) => {
  const isDark = useDarkMode();
  const sorted = useMemo(
    () =>
      Object.entries(tagSummary)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10),
    [tagSummary]
  );

  const chartData = useMemo(
    () => ({
      labels: sorted.map(([name]) => name),
      datasets: [
        {
          label: "OOD Tagged",
          data: sorted.map(([, count]) => count),
          backgroundColor: isDark ? "rgba(192, 132, 252, 0.5)" : "rgba(168, 85, 247, 0.7)",
          borderColor: isDark ? "rgb(192, 132, 252)" : "rgb(168, 85, 247)",
          borderWidth: 1,
        },
      ],
    }),
    [sorted, isDark]
  );

  const gridColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const tickColor = isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)";

  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground mb-2">
        Tag Breakdown
      </p>
      <div className="h-40">
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: "y",
            plugins: { legend: { display: false } },
            scales: {
              x: {
                beginAtZero: true,
                ticks: { color: tickColor },
                grid: { color: gridColor },
              },
              y: {
                ticks: { color: tickColor },
                grid: { color: gridColor },
              },
            },
          }}
        />
      </div>
    </div>
  );
};


const ExampleOodTable: React.FC<{ examples: ExampleOodPoint[] }> = ({
  examples,
}) => (
  <div>
    <p className="text-xs font-medium text-muted-foreground mb-2">
      Example OOD Points
    </p>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-muted-foreground">
            <th className="pb-2 pr-3 w-[50px]">#</th>
            <th className="pb-2 pr-3 w-[80px]">Distance</th>
            <th className="pb-2 pr-3">Summary</th>
            <th className="pb-2 w-[150px]">Tags</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {examples.map((ex, idx) => (
            <tr key={ex.request_id} className="align-top">
              <td className="py-1.5 pr-3 text-muted-foreground tabular-nums text-xs">
                {idx + 1}
              </td>
              <td className="py-1.5 pr-3 tabular-nums font-mono text-xs text-foreground">
                {ex.distance.toFixed(6)}
              </td>
              <td className="py-1.5 pr-3 text-foreground text-xs max-w-md truncate">
                {ex.summary || "—"}
              </td>
              <td className="py-1.5">
                <div className="flex flex-wrap gap-1">
                  {ex.tags.length > 0
                    ? ex.tags.map((t) => (
                        <span
                          key={t}
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-secondary text-secondary-foreground"
                        >
                          {t}
                        </span>
                      ))
                    : "—"}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);


export default ImprovementDetail;
